import { useState, useCallback } from "react";
import { CustomerEntity, GetCustomerByIdUseCase } from "../../domain";
import { CustomerRepositoryImpl } from "../../data/repositories/CustomerRepositoryImpl";

// Create repository instance (singleton)
const customerRepository = new CustomerRepositoryImpl();

// Create use case
const getCustomerByIdUseCase = new GetCustomerByIdUseCase(customerRepository);

export interface UseCustomerDetailsReturn {
  customer: CustomerEntity | null;
  loading: boolean;
  error: string | null;

  fetchCustomer: (customerId: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export const useCustomerDetails = (): UseCustomerDetailsReturn => {
  const [customer, setCustomer] = useState<CustomerEntity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch customer by ID
   */
  const fetchCustomer = useCallback(async (customerId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const result = await getCustomerByIdUseCase.execute(customerId);

      if (!result.success || !result.data) {
        throw new Error("Customer not found");
      }

      setCustomer(result.data);
    } catch (error: any) {
      const errorMessage = error.message || "Failed to load customer";
      setError(errorMessage);
      console.error("Failed to fetch customer:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback((): void => {
    setCustomer(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    customer,
    loading,
    error,
    fetchCustomer,
    clearError,
    reset,
  };
};

